import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getLeague, getCurrentEvent, getNextEvent, updateMemberPayment, removeMember, generateNewInviteCode, getLeaguePickStatus, getLastCompletedEvent, getLeaguePicks, getCompletedEventsWithWinners, leaveLeague, getAllTimeStandings, enterTitleShot } from '../services/api';
import Loading from '../components/Loading';
import UserHoverCard from '../components/UserHoverCard';
import CountdownTimer from '../components/CountdownTimer';
import NextEventCard from '../components/NextEventCard';
import VictoryModal from '../components/VictoryModal';
import OctaAvatar from '../components/OctaAvatar';
import '../styles/app/pool.css';
import { HIDE_MONEY } from '../utils/platform';

function PoolDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pool, setPool] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [nextEvent, setNextEvent] = useState(null);
  const [pickStatus, setPickStatus] = useState(null);
  const [pickStatusLoading, setPickStatusLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [copied, setCopied] = useState(false);
  const [lastCompletedEvent, setLastCompletedEvent] = useState(null);
  const [completedEventWinners, setCompletedEventWinners] = useState([]);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [pastEvents, setPastEvents] = useState([]);
  const [showPastEvents, setShowPastEvents] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [emailsCopied, setEmailsCopied] = useState(false);
  const [allTimeLeaders, setAllTimeLeaders] = useState([]);
  const [enteringTitleShot, setEnteringTitleShot] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [poolRes, eventRes, lastCompletedRes] = await Promise.all([
        getLeague(id),
        getCurrentEvent().catch(() => ({ data: null })),
        getLastCompletedEvent().catch(() => ({ data: null }))
      ]);
      setPool(poolRes.data);
      setCurrentEvent(eventRes.data);

      // Fetch past events that this pool actually participated in
      try {
        const completedEventsRes = await getCompletedEventsWithWinners(id);
        setPastEvents(completedEventsRes.data || []);
      } catch (err) {
        console.error('Failed to fetch past events:', err);
      }

      // Fetch all-time standings for top 3 leaders display
      try {
        const allTimeRes = await getAllTimeStandings(id);
        setAllTimeLeaders(allTimeRes.data?.slice(0, 3) || []);
      } catch (err) {
        console.error('Failed to fetch all-time standings:', err);
      }

      // If current event is completed or open/draft and we have a completed event, fetch next event
      if (eventRes.data?.status === 'completed' || (lastCompletedRes.data && eventRes.data?.status !== 'locked')) {
        const nextRes = await getNextEvent().catch(() => ({ data: null }));
        setNextEvent(nextRes.data);
      }

      // Fetch completed event winners for victory modal from picks data
      // Only set lastCompletedEvent after confirming pool participated
      if (lastCompletedRes.data && poolRes.data) {
        try {
          const picksRes = await getLeaguePicks(id, lastCompletedRes.data.id);
          const responseData = picksRes.data || {};
          const users = responseData.users || [];

          // Check if pool actually participated in this event (at least one pick made)
          const totalPicksForEvent = users.reduce((sum, userData) => {
            return sum + (userData.picks?.length || 0);
          }, 0);

          // Only show results if pool had picks for this event
          if (totalPicksForEvent > 0) {
            // Pool participated - set the completed event
            setLastCompletedEvent(lastCompletedRes.data);

            // Build standings from picks data (use users from picks response, not pool members,
            // since pool members may be filtered to a different event)
            const memberStats = {};
            users.forEach(userData => {
              memberStats[userData.id] = {
                user_id: userData.id,
                username: userData.username,
                display_name: userData.display_name,
                correct_picks: 0,
                total_points: 0,
                method_bonus: 0
              };
              const userPicks = userData.picks || [];
              userPicks.forEach(pick => {
                if (pick.is_correct) {
                  memberStats[userData.id].correct_picks++;
                  memberStats[userData.id].total_points += pick.points_earned || 0;
                  memberStats[userData.id].method_bonus += pick.method_bonus || 0;
                }
              });
            });

            const sorted = Object.values(memberStats)
              .sort((a, b) => b.total_points - a.total_points || b.correct_picks - a.correct_picks);
            // Assign ranks with tie handling
            const standings = [];
            sorted.forEach((entry, index) => {
              let rank = index + 1;
              if (index > 0) {
                const prev = sorted[index - 1];
                if (entry.total_points === prev.total_points && entry.correct_picks === prev.correct_picks) {
                  rank = standings[index - 1].rank;
                }
              }
              standings.push({ ...entry, rank, points: entry.total_points });
            });

            setCompletedEventWinners(standings);

            // Check if should show victory modal
            const dismissedKey = `celebration-dismissed-${id}-${lastCompletedRes.data.id}`;
            if (!localStorage.getItem(dismissedKey)) {
              setShowVictoryModal(true);
            }
          }
        } catch (err) {
          console.error('Failed to fetch completed event winners:', err);
        }
      }

      if (eventRes.data) {
        setPickStatusLoading(true);
        const statusRes = await getLeaguePickStatus(id, eventRes.data.id);
        const statusMap = {};
        statusRes.data.submissions.forEach((entry) => {
          statusMap[entry.user_id] = entry;
        });
        setPickStatus(statusMap);
        setPickStatusLoading(false);
      } else {
        setPickStatus(null);
        setPickStatusLoading(false);
      }
    } catch (error) {
      const status = error.response?.status;
      // 403/404 means not a member or pool gone — bounce to dashboard.
      if (status === 403 || status === 404) {
        navigate('/dashboard');
        return;
      }
      setLoadError(error.response?.data?.error || 'Couldn\'t load this pool. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const isCommissioner = pool?.commissioner_id === user?.id;
  const isGlobal = pool?.is_global === true;
  const inviteUrl = `${process.env.REACT_APP_BASE_URL || window.location.origin}/join/${pool?.invite_code}`;

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareInvite = async () => {
    // Include URL in text only (no separate url param) so iOS sends as one message
    const shareText = isGlobal
      ? `Join the PNP Title Shot — free MMA pick'em on Pick 'N Pray. Pick the winner and method for every fight; the winner takes home the Title Shot belt.\n\n${inviteUrl}`
      : HIDE_MONEY
        ? `Join my MMA pick'em pool "${pool.name}" on Pick 'N Pray!\n\n${inviteUrl}`
        : `Join my MMA pick'em pool "${pool.name}" on Pick 'N Pray!\n\nEntry fee: $${pool.entry_fee}/card\n\n${inviteUrl}`;

    if (navigator.share) {
      try {
        await navigator.share({
          text: shareText
        });
      } catch (err) {
        // User cancelled or share failed, fall back to copy
        if (err.name !== 'AbortError') {
          copyInviteLink();
        }
      }
    } else {
      // Fallback for browsers without Web Share API
      copyInviteLink();
    }
  };

  const handleTogglePayment = async (memberId, currentStatus) => {
    try {
      await updateMemberPayment(id, memberId, !currentStatus);
      fetchData();
    } catch (error) {
      console.error('Failed to update payment:', error);
    }
  };

  const handleRemoveMember = async (memberId, username) => {
    if (!window.confirm(`Remove ${username} from the pool?`)) return;
    try {
      await removeMember(id, memberId);
      fetchData();
    } catch (error) {
      console.error('Failed to remove member:', error);
    }
  };

  const handleNewInviteCode = async () => {
    if (!window.confirm('Generate a new invite code? The old code will stop working.')) return;
    try {
      const response = await generateNewInviteCode(id);
      setPool({ ...pool, invite_code: response.data.invite_code });
    } catch (error) {
      console.error('Failed to generate new code:', error);
    }
  };

  const handleDismissVictory = () => {
    if (lastCompletedEvent?.id) {
      const dismissedKey = `celebration-dismissed-${id}-${lastCompletedEvent.id}`;
      localStorage.setItem(dismissedKey, 'true');
    }
    setShowVictoryModal(false);
  };

  const handleEnterTitleShot = async () => {
    setEnteringTitleShot(true);
    try {
      await enterTitleShot();
      fetchData();
    } catch (error) {
      console.error('Failed to enter Title Shot:', error);
      alert('Failed to enter Title Shot. Please try again.');
    } finally {
      setEnteringTitleShot(false);
    }
  };

  const handleLeavePool = async () => {
    if (!window.confirm('Are you sure you want to leave this pool? You will need a new invite to rejoin.')) return;
    try {
      await leaveLeague(id);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to leave pool:', error);
      alert('Failed to leave pool. Please try again.');
    }
  };

  // Member selection for copying emails (commissioner only)
  const toggleMemberSelection = (memberId) => {
    setSelectedMemberIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
    setEmailsCopied(false);
  };

  const toggleAllMembersSelection = () => {
    if (selectedMemberIds.size === pool?.members?.length) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(pool?.members?.map(m => m.id) || []));
    }
    setEmailsCopied(false);
  };

  const getSelectedMemberEmails = () => {
    const emails = pool?.members
      ?.filter(m => selectedMemberIds.has(m.id) && m.email)
      .map(m => m.email) || [];
    return [...new Set(emails)];
  };

  const copySelectedEmails = () => {
    const emails = getSelectedMemberEmails();
    if (emails.length === 0) return;
    navigator.clipboard.writeText(emails.join(', '));
    setEmailsCopied(true);
    setTimeout(() => setEmailsCopied(false), 2000);
  };


  if (loading) return <Loading />;
  if (loadError) {
    return (
      <div className="container pd-page">
        <div className="pnp-empty pd-error">
          <div className="pnp-empty-icon" aria-hidden="true"><IconAlert /></div>
          <h3 className="pnp-empty-title">Couldn't load this pool</h3>
          <p>{loadError}</p>
          <div className="pnp-empty-actions">
            <button onClick={fetchData} className="btn btn-primary">Try again</button>
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">Back to dashboard</button>
          </div>
        </div>
      </div>
    );
  }
  if (!pool) return null;

  // Entry fee / paid / Venmo UI only exists on the web (HIDE_MONEY on native).
  const showMoney = !HIDE_MONEY && pool.entry_fee > 0;
  const paidCount = pool.members?.filter(m => m.has_paid).length || 0;
  const totalPot = paidCount * pool.entry_fee;
  const commissioner = pool.members?.find(member => member.id === pool.commissioner_id);
  const venmoHandle = commissioner?.venmo_handle?.replace('@', '');
  const venmoUrl = venmoHandle ? `https://venmo.com/u/${venmoHandle}` : null;
  const venmoQrUrl = venmoUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(venmoUrl)}`
    : null;

  const memberCount = pool.members?.length || 0;
  const myStatus = pickStatus?.[user?.id];
  const myPicksCount = myStatus?.picks_count || 0;
  const myExpectedCount = myStatus?.expected_count || 0;
  const myPicksPct = myExpectedCount > 0 ? Math.min(100, (myPicksCount / myExpectedCount) * 100) : 0;

  const poolStats = isGlobal
    ? [
        { label: 'Competitors', value: memberCount },
        HIDE_MONEY
          ? { label: 'Format', value: "Pick'em" }
          : { label: 'Entry', value: 'Free', tone: 'pd-stat-win' },
        { label: 'Fights', value: 'All' },
        { label: 'Prize', value: 'Belt', tone: 'pd-stat-gold', icon: '/images/title-belt.svg' },
      ]
    : [
        { label: 'Members', value: memberCount },
        ...(showMoney ? [
          { label: 'Entry Fee / Card', value: `$${pool.entry_fee}` },
          { label: 'This Event\'s Pot', value: `$${totalPot}`, tone: 'pd-stat-win' },
          { label: 'Paid (This Event)', value: `${paidCount}/${memberCount}` },
        ] : []),
      ];

  // Pick status for one member row (same conditions as before, shown as a status dot)
  const renderMemberPicks = (member) => {
    if (!currentEvent) {
      return <span className="pd-member-picks pd-st-idle"><span className="pd-dot" />—</span>;
    }
    if (pickStatusLoading) {
      return (
        <span className="pd-member-picks pd-st-idle">
          <span className="pnp-skel pd-skel-inline" aria-hidden="true" />
          <span className="sr-only">Checking...</span>
        </span>
      );
    }
    const status = pickStatus?.[member.id];
    if (status?.submitted) {
      const full = status.picks_count >= status.expected_count;
      return (
        <span className={`pd-member-picks ${full ? 'pd-st-in' : 'pd-st-part'}`}>
          <span className="pd-dot" />
          {status.picks_count}/{status.expected_count}
        </span>
      );
    }
    return (
      <span className="pd-member-picks pd-st-none">
        <span className="pd-dot" />
        0/{status?.expected_count || '?'}
      </span>
    );
  };

  return (
    <div className="container pd-page">
      <Link to="/dashboard" className="pd-back">
        <IconArrowLeft /> Back to Dashboard
      </Link>

      {/* ---------- Hero ---------- */}
      <section className={`pd-hero pnp-rise${isGlobal ? ' pd-hero-gold' : ''}`}>
        <span className="pnp-glow pd-hero-glow" aria-hidden="true" />
        <span className={`pnp-claw pd-hero-claw${isGlobal ? ' pnp-claw-gold' : ''}`} aria-hidden="true" />

        <div className="pd-hero-top">
          <div className="pd-hero-id">
            <p className={`pnp-eyebrow${isGlobal ? ' pnp-eyebrow-gold' : ''}`}>
              {isGlobal ? 'Global Pool' : 'Private Pool'}
            </p>
            <div className="pd-hero-name">
              {isGlobal && <img src="/images/title-belt.svg" alt="Title Belt" className="pd-hero-belt" />}
              <h1 className="pd-hero-title">{pool.name}</h1>
            </div>
            {pool.description && (
              <p className="pd-hero-desc">{pool.description}</p>
            )}
            {!isGlobal && commissioner && (
              <div className="pd-hero-chips">
                <span className="pnp-chip pnp-chip-red">
                  <IconCrown />
                  Commissioner · {isCommissioner ? 'You' : commissioner.username}
                </span>
              </div>
            )}
          </div>

          {!isGlobal && isCommissioner && (
            <div className="pd-hero-actions">
              {!showInvite && pool.members?.length <= 2 && (
                <span className="get-started-hint-inline">
                  Invite your friends! <span className="bounce-arrow-right">→</span>
                </span>
              )}
              <button className="btn btn-outline" onClick={() => setShowInvite(!showInvite)}>
                <IconUserPlus /> Invite Members
              </button>
              <Link to={`/pool/${id}/settings`} className="btn btn-secondary">
                <IconSettings /> Pool Settings
              </Link>
            </div>
          )}
          {isGlobal && (
            <div className="pd-hero-actions">
              <button className="btn btn-secondary" onClick={shareInvite}>
                <IconShare /> Invite Others
              </button>
            </div>
          )}
        </div>

        {/* Member avatar stack */}
        {!isGlobal && pool.members?.length > 0 && (
          <div className="pd-crew">
            <div className="pd-crew-stack">
              {pool.members.slice(0, 6).map((m, i) => (
                <div key={m.id} style={{ marginLeft: i ? '-10px' : 0, zIndex: 6 - i }}>
                  <OctaAvatar name={m.username} size={36} bg={memberAvatarColor(m.id)} ring ringColor="rgba(255,255,255,0.08)" />
                </div>
              ))}
              {pool.members.length > 6 && (
                <div className="pd-crew-more">+{pool.members.length - 6}</div>
              )}
              <span className="pd-crew-label">{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
            </div>
            {isCommissioner && (
              <button className="btn btn-secondary btn-sm" onClick={() => setShowInvite(!showInvite)}>
                + Invite
              </button>
            )}
          </div>
        )}

        <div className={`pnp-stats pd-stats pd-stats-${poolStats.length}`}>
          {poolStats.map(stat => (
            <div key={stat.label} className="pnp-stat">
              <span className={`pnp-stat-value${stat.tone ? ` ${stat.tone}` : ''}${typeof stat.value === 'string' && !/\d/.test(stat.value) ? ' pd-stat-word' : ''}`}>
                {stat.icon && <img src={stat.icon} alt="" />}
                {stat.value}
              </span>
              <span className="pnp-stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Quick action tiles */}
      {!isGlobal && (
        <div className="pd-tiles pnp-rise" style={{ '--i': 1 }}>
          <ActionTile
            label="My Picks"
            value={currentEvent && pickStatus?.[user?.id] ? `${pickStatus[user.id].picks_count}/${pickStatus[user.id].expected_count}` : '—'}
            tone="green"
            numeric
            onClick={() => navigate(`/pool/${id}/picks`)}
          />
          <ActionTile
            label="Standings"
            value="View"
            tone="red"
            onClick={() => navigate(`/pool/${id}/leaderboard`)}
          />
          <ActionTile
            label="Corner"
            value="Chat"
            tone="gold"
            onClick={() => navigate('/corner-chat')}
          />
        </div>
      )}

      {/* Invite panel */}
      {isCommissioner && showInvite && (
        <section className="pd-card pd-invite pnp-rise">
          <div className="pd-card-head">
            <div>
              <p className="pnp-eyebrow">Bring your crew</p>
              <h3 className="pd-card-title">Invite Code</h3>
            </div>
            {isCommissioner && (
              <button className="btn btn-ghost btn-sm" onClick={handleNewInviteCode}>
                Regenerate
              </button>
            )}
          </div>
          <div className="pd-invite-row">
            <div className="pd-code-pill">
              <span className="pd-code">{pool.invite_code}</span>
              <button className="btn btn-primary btn-sm" onClick={copyInviteLink}>
                <IconCopy /> {copied ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
          <div className="pd-invite-url">
            <input
              type="text"
              className="form-input"
              value={inviteUrl}
              readOnly
              aria-label="Invite link"
            />
            <button className="btn btn-primary" onClick={shareInvite}>
              <IconShare /> Share Invite
            </button>
          </div>
        </section>
      )}

      <div className="pd-grid">
        {/* ---------- Left column: event, results, leaders, history, scoring ---------- */}
        <div className="pd-col">
          {/* Make Picks Section - Always Prominent */}
          <section className={`pd-card pd-event pd-o-event pnp-rise${isGlobal ? ' pd-event-gold' : ''}`} style={{ '--i': 2 }}>
            <span className={`pnp-claw pd-event-claw${isGlobal ? ' pnp-claw-gold' : ''}`} aria-hidden="true" />
            <div className="pd-event-top">
              <p className={`pnp-eyebrow${isGlobal ? ' pnp-eyebrow-gold' : ''}`}>
                {isGlobal ? 'PNP Title Shot' : 'Fight Night'}
              </p>
              {currentEvent?.status === 'open' && (
                <span className="pnp-status" style={{ color: 'var(--win)' }}>
                  <span className="pnp-live-dot pnp-live-dot-green" /> Picks Open
                </span>
              )}
              {currentEvent?.status === 'locked' && (
                <span className="pnp-status" style={{ color: 'var(--belt)' }}>
                  <span className="pnp-status-dot" /> Picks Locked
                </span>
              )}
            </div>
            <h2 className="pd-event-title">
              {currentEvent ? currentEvent.name : 'Make Your Picks'}
            </h2>
            {currentEvent ? (
              <p className="pd-event-date">
                {new Date(currentEvent.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            ) : (
              <p className="pd-event-empty">
                No active event right now. Check back soon!
              </p>
            )}
            {currentEvent?.status === 'open' && (
              <CountdownTimer targetDate={currentEvent.date} label="Picks close in" />
            )}

            {currentEvent && myStatus && (
              <div className="pd-progress">
                <div className="pd-progress-row">
                  <span className="pd-progress-label">Your picks</span>
                  <span className="pd-progress-count">
                    {myPicksCount}<small>/{myExpectedCount || '?'}</small>
                  </span>
                </div>
                <div className={`pd-bar${myExpectedCount > 0 && myPicksCount >= myExpectedCount ? ' is-full' : ''}`}>
                  <span style={{ width: `${myPicksPct}%` }} />
                </div>
              </div>
            )}

            <div className="pd-ctas">
              {currentEvent && isGlobal && !pool.members?.find(m => m.id === user?.id) && currentEvent.status === 'open' ? (
                <button
                  className="btn btn-gold btn-lg"
                  disabled={enteringTitleShot}
                  onClick={handleEnterTitleShot}
                >
                  {enteringTitleShot ? 'Entering...' : 'Enter Title Shot'}
                </button>
              ) : currentEvent && (
                <>
                  <Link to={`/pool/${id}/picks`} className="btn btn-primary btn-lg">
                    {currentEvent.status === 'locked' || currentEvent.status === 'completed'
                      ? 'View Picks'
                      : pickStatus?.[user?.id]?.submitted ? 'Edit Picks' : 'Make Picks'}
                  </Link>
                  {pickStatus?.[user?.id]?.submitted && (
                    <Link
                      to={`/lock-sheet?event=${currentEvent.id}&pool=${id}`}
                      className="btn btn-secondary"
                    >
                      <IconShare /> Share Picks
                    </Link>
                  )}
                  <Link to={`/pool/${id}/leaderboard`} className="btn btn-secondary">
                    Leaderboard
                  </Link>
                </>
              )}
            </div>
          </section>

          {/* Show next event card when current is completed */}
          {currentEvent?.status === 'completed' && nextEvent && (
            <div className="pd-slot pd-o-next">
              <NextEventCard event={nextEvent} />
            </div>
          )}

          {/* Completed Event Results Card - show for 24 hours after completion, hide when picks are locked */}
          {lastCompletedEvent && currentEvent?.status !== 'locked' && (() => {
            const completedTimestamp = lastCompletedEvent.completed_at || lastCompletedEvent.date;
            const isWithin24Hours = completedTimestamp &&
              (new Date().getTime() - new Date(completedTimestamp).getTime()) < 24 * 60 * 60 * 1000;
            return isWithin24Hours;
          })() && (
            <section className="pd-card pd-results pd-o-results pnp-rise" style={{ '--i': 3 }}>
              <div className="pd-results-head">
                <div className="pd-results-icon" aria-hidden="true"><IconTrophy /></div>
                <div style={{ minWidth: 0 }}>
                  <h3 className="pd-results-title">{lastCompletedEvent.name} Results</h3>
                  <p className="pd-results-date">
                    {new Date(lastCompletedEvent.date).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
              {/* Top 3 Winners Preview */}
              {completedEventWinners.length > 0 && (
                <div className="pd-podium">
                  {completedEventWinners.slice(0, 3).map((winner, idx) => (
                    <div key={winner.user_id} className="pd-podium-row">
                      <span className={`pd-rank pd-rank-${idx + 1}`}>{idx + 1}</span>
                      <span className="pd-podium-name">
                        <UserHoverCard userId={winner.user_id} username={winner.username}>
                          <span>{winner.username}</span>
                        </UserHoverCard>
                      </span>
                      <span className="pd-pts">
                        {winner.total_points || winner.points} pts
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <Link
                to={`/pool/${id}/leaderboard?event=${lastCompletedEvent.id}`}
                className="btn pd-btn-win"
              >
                View Full Results
              </Link>
            </section>
          )}

          {/* All-Time Leaders - Top 3 */}
          {allTimeLeaders.length > 0 && (
            <section className="pd-card pd-o-leaders pnp-rise" style={{ '--i': 3 }}>
              <div className="pd-card-head">
                <div>
                  <p className="pnp-eyebrow pnp-eyebrow-gold">Standings</p>
                  <h2 className="pd-card-title">All-Time Leaders</h2>
                </div>
                <Link to={`/pool/${id}/leaderboard#historical-standings`} className="pd-link">
                  View Full Standings <IconArrowRight />
                </Link>
              </div>
              <div className="pd-leaders">
                {allTimeLeaders.map((leader, index) => {
                  const rank = index + 1;
                  const isCurrentUser = leader.user_id === user?.id;

                  return (
                    <div
                      key={leader.user_id}
                      className={`pd-leader pd-leader-${rank}${isCurrentUser ? ' is-you' : ''}`}
                    >
                      <div className="pd-leader-avatar">
                        <OctaAvatar name={leader.username} size={44} bg={memberAvatarColor(leader.user_id)} />
                        <span className={`pd-rank pd-rank-${rank}`}>{rank}</span>
                      </div>
                      <div className="pd-leader-info">
                        <div className="pd-leader-name">
                          <span>
                            <UserHoverCard userId={leader.user_id} username={leader.username}>
                              <span style={{ cursor: 'pointer' }}>{leader.username}</span>
                            </UserHoverCard>
                          </span>
                          {isCurrentUser && <span className="pd-you">YOU</span>}
                        </div>
                        <div className="pd-leader-stats">
                          <span><strong>{leader.correct_picks || 0}/{leader.total_picks || 0}</strong> picks · {leader.pick_accuracy || 0}%</span>
                          {leader.total_method_picks > 0 && (
                            <span><strong>{leader.correct_methods || 0}/{leader.total_method_picks || 0}</strong> methods · {leader.method_accuracy || 0}%</span>
                          )}
                          {leader.wins > 0 && (
                            <span className="pd-leader-belts">
                              <img src="/images/title-belt.svg" alt="" />
                              ×{leader.wins}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="pd-leader-pts">
                        <b>{leader.total_points}</b>
                        <span>pts</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* Past Events Section */}
          {pastEvents.length > 0 && (
            <section className="pd-card pd-o-past">
              <button
                type="button"
                className="pd-past-toggle"
                aria-expanded={showPastEvents}
                onClick={() => setShowPastEvents(!showPastEvents)}
              >
                <h2 className="pd-card-title">Past Events</h2>
                <span className="pd-past-count">
                  {pastEvents.length} event{pastEvents.length !== 1 ? 's' : ''}
                  <span className="pd-chev" aria-hidden="true"><IconChevron /></span>
                </span>
              </button>
              {showPastEvents && (
                <div className="pd-past-list">
                  {pastEvents.map((event, i) => (
                    <div key={event.id} className="pd-past-row pnp-rise" style={{ '--i': Math.min(i, 8) }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="pd-past-name">{event.name}</div>
                        <div className="pd-past-date">
                          {new Date(event.date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </div>
                      <div className="pd-past-btns">
                        <Link
                          to={`/pool/${id}/leaderboard?event=${event.id}`}
                          className="btn btn-secondary btn-sm"
                        >
                          View Results
                        </Link>
                        <Link
                          to={`/pool/${id}/picks?event=${event.id}`}
                          className="btn btn-secondary btn-sm"
                        >
                          View Picks
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
          {/* Scoring Summary — one row per fight tier */}
          {(() => {
            const inclusion = pool.fight_inclusion || (pool.include_all_fights === 0 ? 'main' : 'all');
            const methodFor = pool.method_required_for || 'none';
            const methodBonus = pool.method_bonus_points || 3;
            const titleMultiplier = pool.championship_multiplier || 1;

            const types = [
              inclusion === 'all' || inclusion === 'main_prelims' || inclusion === 'main' ? {
                key: 'main',
                label: 'Main Card',
                base: 5,
                requiresMethod: methodFor === 'main' || methodFor === 'main_prelims' || methodFor === 'all',
                includeTitle: titleMultiplier > 1,
              } : null,
              inclusion === 'all' || inclusion === 'main_prelims' ? {
                key: 'prelim',
                label: 'Prelims',
                base: 3,
                requiresMethod: methodFor === 'main_prelims' || methodFor === 'all',
                includeTitle: false,
              } : null,
              inclusion === 'all' ? {
                key: 'early',
                label: 'Early Prelims',
                base: 1,
                requiresMethod: methodFor === 'all',
                includeTitle: false,
              } : null,
            ].filter(Boolean);

            return (
              <section className="pd-card pd-scoring pd-o-scoring">
                <p className="pnp-eyebrow pnp-eyebrow-mute">Scoring</p>
                {types.map(t => (
                  <div key={t.key} className={`pd-tier pd-tier-${t.key}`}>
                    <div className="pd-tier-info">
                      <div className="pd-tier-label">{t.label}</div>
                      <div className="pd-tier-meta">
                        {t.requiresMethod ? (
                          <span className="pd-tier-bonus">+{methodBonus} method bonus</span>
                        ) : (
                          <span>Method optional</span>
                        )}
                        {t.includeTitle && (
                          <>
                            <span className="pd-tier-sep">•</span>
                            <span className="pd-tier-title">Title × {titleMultiplier}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="pd-tier-pts">
                      <b>{t.base}</b>
                      <span>pt{t.base !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
              </section>
            );
          })()}

        </div>

        {/* ---------- Right column: members, payment, leave ---------- */}
        <div className="pd-col">
          {isGlobal ? (
            <section className="pd-card pd-o-members pnp-rise" style={{ '--i': 2 }}>
              <div className="pd-card-head">
                <h2 className="pd-card-title">Competitors</h2>
                <span className="pd-card-meta">{pool.members?.length || 0} entered</span>
              </div>
              {pool.members?.length > 0 ? (
                <div className="pd-member-list">
                  {pool.members.map((member, index) => {
                    const isMe = member.id === user?.id;
                    return (
                      <div key={member.id} className={`pd-member pd-comp${isMe ? ' is-you' : ''}`}>
                        <span className="pd-comp-rank">{index + 1}</span>
                        <div className="pd-member-id">
                          <UserHoverCard userId={member.id} username={member.username}>
                            <span className="pd-member-link">
                              <OctaAvatar name={member.username} size={32} bg={memberAvatarColor(member.id)} />
                              <span className="pd-member-name">{member.username?.toUpperCase()}</span>
                            </span>
                          </UserHoverCard>
                        </div>
                        {!currentEvent ? (
                          <span className="pd-member-picks pd-st-idle">—</span>
                        ) : pickStatusLoading ? (
                          <span className="pd-member-picks pd-st-idle">
                            <span className="pnp-skel pd-skel-inline" aria-hidden="true" />
                            <span className="sr-only">Checking...</span>
                          </span>
                        ) : (() => {
                          const status = pickStatus?.[member.id];
                          const picksCount = status?.picks_count || 0;
                          const expectedCount = status?.expected_count || 0;
                          const allIn = status?.submitted && picksCount >= expectedCount && expectedCount > 0;
                          const progressPct = expectedCount > 0 ? Math.min(100, (picksCount / expectedCount) * 100) : 0;
                          return (
                            <div className="pd-comp-picks">
                              <div className={`pd-bar ${allIn ? 'is-full' : 'is-gold'}`}>
                                <span style={{ width: `${progressPct}%` }} />
                              </div>
                              <span className={`pd-comp-count ${allIn ? 'pd-st-in' : expectedCount > 0 ? 'pd-st-part' : 'pd-st-idle'}`}>
                                {picksCount}/{expectedCount || '?'}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="pd-empty-line">No competitors yet — be the first to enter!</p>
              )}
            </section>
          ) : (
            <section className="pd-card pd-o-members pnp-rise" style={{ '--i': 2 }}>
              <div className="pd-card-head">
                <h2 className="pd-card-title">Members</h2>
                <span className="pd-card-meta">{memberCount}</span>
              </div>
              {(!HIDE_MONEY || isCommissioner) && (
              <div className="pd-members-tools">
                {!HIDE_MONEY && (
                  <div className="pd-payout">
                    Payout: <b>{pool.payout_first}% / {pool.payout_second}% / {pool.payout_third}%</b>
                  </div>
                )}
                {isCommissioner && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', ...(HIDE_MONEY ? { marginLeft: 'auto' } : {}) }}>
                    {selectedMemberIds.size > 0 && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={copySelectedEmails}
                      >
                        {emailsCopied ? 'Copied!' : `Copy ${getSelectedMemberEmails().length} Email${getSelectedMemberEmails().length !== 1 ? 's' : ''}`}
                      </button>
                    )}
                    <label className="pd-select-all">
                      <input
                        type="checkbox"
                        className="pd-check"
                        checked={selectedMemberIds.size === pool.members?.length && pool.members?.length > 0}
                        onChange={toggleAllMembersSelection}
                      />
                      All
                    </label>
                  </div>
                )}
              </div>
              )}

              <div className="pd-member-list">
                {pool.members?.map(member => {
                  const isSelected = selectedMemberIds.has(member.id);
                  const isMe = member.id === user?.id;
                  return (
                    <div
                      key={member.id}
                      className={`pd-member${isCommissioner ? ' has-check' : ''}${isSelected ? ' is-selected' : ''}${isMe ? ' is-you' : ''}`}
                    >
                      {isCommissioner && (
                        <input
                          type="checkbox"
                          className="pd-check"
                          aria-label={`Select ${member.username}`}
                          checked={isSelected}
                          onChange={() => toggleMemberSelection(member.id)}
                        />
                      )}
                      <div className="pd-member-id">
                        <UserHoverCard userId={member.id} username={member.username}>
                          <span className="pd-member-link">
                            <OctaAvatar name={member.username} size={36} bg={memberAvatarColor(member.id)} />
                            <span className="pd-member-text">
                              <span className="pd-member-name">{member.username}</span>
                              {member.email && <span className="pd-member-email">{member.email}</span>}
                            </span>
                          </span>
                        </UserHoverCard>
                      </div>
                      {renderMemberPicks(member)}
                      <div className="pd-member-meta">
                        {member.id === pool.commissioner_id ? (
                          <span className="pnp-chip pnp-chip-red">Commissioner</span>
                        ) : (
                          <span className="pnp-chip pnp-chip-mute">Member</span>
                        )}
                        {showMoney && (
                          member.has_paid ? (
                            <span className="pnp-chip pnp-chip-green">Paid</span>
                          ) : (
                            <span className="pnp-chip pnp-chip-gold">Unpaid</span>
                          )
                        )}
                        {isCommissioner && (showMoney || member.id !== pool.commissioner_id) && (
                          <div className="pd-member-actions">
                            {showMoney && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleTogglePayment(member.id, member.has_paid)}
                              >
                                {member.has_paid ? 'Mark Unpaid' : 'Mark Paid'}
                              </button>
                            )}
                            {member.id !== pool.commissioner_id && (
                              <button
                                className="btn btn-danger btn-sm"
                                onClick={() => handleRemoveMember(member.id, member.username)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {commissioner && !isGlobal && showMoney && (
            <section className="pd-card pd-o-venmo">
              <div className="pd-card-head">
                <div>
                  <p className="pnp-eyebrow pnp-eyebrow-mute">Settle up</p>
                  <h2 className="pd-card-title">Commissioner Payment</h2>
                </div>
              </div>
              {venmoUrl ? (
                <div className="pd-venmo">
                  <img
                    src={venmoQrUrl}
                    alt="Commissioner Venmo QR"
                    className="pd-venmo-qr"
                  />
                  <div className="pd-venmo-info">
                    <p className="pd-venmo-handle">
                      Venmo: <strong>@{venmoHandle}</strong>
                    </p>
                    <a href={venmoUrl} target="_blank" rel="noreferrer" className="pd-venmo-link">
                      {venmoUrl}
                    </a>
                  </div>
                </div>
              ) : (
                <p className="pd-muted">
                  Commissioner has not added a Venmo handle yet.
                </p>
              )}
            </section>
          )}

          {/* Leave Pool - Only for non-commissioners, not for global pool */}
          {!isCommissioner && !isGlobal && (
            <section className="pd-card pd-leave pd-o-leave">
              <h3 className="pd-card-title">Leave Pool</h3>
              <p className="pd-muted">
                If you leave, you'll need a new invite link to rejoin. Your pick history will be preserved.
              </p>
              <button
                className="btn btn-danger"
                onClick={handleLeavePool}
              >
                Leave Pool
              </button>
            </section>
          )}
        </div>
      </div>

      {/* Victory Modal - Show for completed event */}
      {showVictoryModal && lastCompletedEvent && (
        <VictoryModal
          event={lastCompletedEvent}
          standings={completedEventWinners}
          onClose={handleDismissVictory}
          poolName={pool?.name}
          entryFee={pool?.entry_fee || 0}
          memberCount={pool?.members?.filter(m => m.has_paid).length || 0}
          payoutFirst={pool?.payout_first || 60}
          payoutSecond={pool?.payout_second || 30}
          payoutThird={pool?.payout_third || 10}
          nextEvent={nextEvent}
          isGlobal={isGlobal}
        />
      )}

    </div>
  );
}

const MEMBER_AVATAR_COLORS = ['var(--primary)', '#7d5cff', 'var(--gold)', 'var(--cyan)', 'var(--success)'];
const memberAvatarColor = (userId) => MEMBER_AVATAR_COLORS[userId % MEMBER_AVATAR_COLORS.length];

function ActionTile({ label, value, tone, numeric, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pd-tile pd-tile-${tone}`}
    >
      <span className="pd-tile-label">{label}</span>
      <span className={`pd-tile-value${numeric ? ' is-num' : ''}`}>
        {value}
        <IconArrowRight />
      </span>
    </button>
  );
}

/* ---- Inline icons (stroke = currentColor) ---- */
const svgProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
};

const IconArrowLeft = () => (
  <svg {...svgProps}><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
);
const IconArrowRight = () => (
  <svg {...svgProps} width="14" height="14"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);
const IconChevron = () => (
  <svg {...svgProps} strokeWidth={2.6}><polyline points="9 6 15 12 9 18" /></svg>
);
const IconShare = () => (
  <svg {...svgProps}><path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" /></svg>
);
const IconCopy = () => (
  <svg {...svgProps}><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
);
const IconUserPlus = () => (
  <svg {...svgProps}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><line x1="19" y1="8" x2="19" y2="14" /><line x1="22" y1="11" x2="16" y2="11" /></svg>
);
const IconSettings = () => (
  <svg {...svgProps}><line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" /><line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" /><line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" /><line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" /></svg>
);
const IconCrown = () => (
  <svg {...svgProps}><path d="M2 18h20L19 7l-5 5-2-7-2 7-5-5z" /></svg>
);
const IconTrophy = () => (
  <svg {...svgProps}><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0V4z" /><path d="M17 5h3v2a3 3 0 0 1-3 3" /><path d="M7 5H4v2a3 3 0 0 0 3 3" /></svg>
);
const IconAlert = () => (
  <svg {...svgProps} width="30" height="30"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
);

export default PoolDetail;
